import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, ChevronDown, ChevronUp, RefreshCw, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AlertasPage = ({ user, onLogout }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [detailData, setDetailData] = useState({});

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/absence-alerts`, { headers });
      setAlerts(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const toggleExpand = async (alertId) => {
    if (expandedAlert === alertId) { setExpandedAlert(null); return; }
    setExpandedAlert(alertId);
    if (!detailData[alertId]) {
      try {
        const res = await axios.get(`${API}/absence-alerts/${alertId}/detail`, { headers });
        setDetailData(prev => ({ ...prev, [alertId]: res.data }));
      } catch (e) { console.error(e); }
    }
  };

  const handleAttend = async (alertId) => {
    if (!window.confirm('¿Marcar como atendido? Se eliminará esta alerta.')) return;
    try {
      await axios.delete(`${API}/absence-alerts/${alertId}/attend`, { headers });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (e) { console.error(e); }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <AlertTriangle className="w-7 h-7 text-red-500" />
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-testid="alertas-title">Alertas de Inasistencias</h1>
            <p className="text-sm text-slate-600">Alumnos con 5 o más faltas en alguna materia</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-red-400" /></div>
        ) : alerts.length === 0 ? (
          <Card className="p-12 text-center">
            <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No hay alertas pendientes</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {alerts.map(alert => (
              <Card key={alert.id} className="border-l-4 border-l-red-500 overflow-hidden" data-testid={`alert-${alert.id}`}>
                <div className="p-4 cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => toggleExpand(alert.id)}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-red-600">{alert.is_recurrence ? 'REINCIDENCIA' : 'ALERTA'}</span>
                        <span className="text-sm text-slate-600">|</span>
                        <span className="font-semibold text-slate-900">{alert.student_name}</span>
                      </div>
                      <p className="text-sm text-slate-600 mt-0.5">
                        {alert.absence_count} faltas en <span className="font-medium">{alert.triggering_class_subject}</span> ({alert.triggering_class_group})
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {new Date(alert.triggered_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); handleAttend(alert.id); }} data-testid={`attend-${alert.id}`}>
                        <Trash2 className="w-4 h-4 mr-1" /> Atendido
                      </Button>
                      {expandedAlert === alert.id ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>
                </div>
                {expandedAlert === alert.id && detailData[alert.id] && (
                  <div className="border-t bg-slate-50 p-4">
                    <p className="text-xs font-semibold text-slate-500 mb-2 uppercase">Detalle por materia</p>
                    <div className="space-y-2">
                      {detailData[alert.id].class_details.map(cd => (
                        <div key={cd.class_id} className={`flex items-center justify-between p-2 rounded ${cd.is_trigger ? 'bg-red-50 border border-red-200' : 'bg-white border'}`}>
                          <div>
                            <span className="font-medium text-sm">{cd.subject}</span>
                            {cd.is_trigger && <span className="ml-2 text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">ORIGEN</span>}
                            <p className="text-xs text-slate-500">Prof. {cd.teacher_name}</p>
                          </div>
                          <div className="flex gap-4 text-xs">
                            <span className={cd.absences >= 5 ? 'text-red-600 font-bold' : 'text-slate-600'}>Faltas: {cd.absences}/{cd.total_classes}</span>
                            <span className="text-slate-600">Tareas: {cd.tasks_completed}/{cd.total_tasks}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AlertasPage;
